import { injectable } from 'inversify'
import type { Command } from '@/plugins/cad/module/core/Command'
import EventDispatcher from '@/plugins/cad/module/core/common/EventDispatcher'

/**
 * CAD 历史管理器，维护可撤销/重做的命令栈与交互中的活动命令。
 */
@injectable()
export class HistoryManager extends EventDispatcher {
  private undoStack: Command[] = []
  private redoStack: Command[] = []
  // 限制历史记录大小
  private readonly maxLimit = 10

  // 当前正在交互但尚未“提交”的命令（如：绘制线段）
  private activeCommand: Command | null = null
  // 锁定状态，在某些交互场景下禁止撤销/重做
  private isLocked: boolean = false

  /** 是否可以撤销(可用于UI状态绑定) */
  get canUndo() {
    return (
      !this.isLocked &&
      (this.activeCommand
        ? (this.activeCommand.canUndoStep?.() ?? false)
        : this.undoStack.length > 0)
    )
  }

  /** 是否可以重做(可用于UI状态绑定) */
  get canRedo() {
    return !this.isLocked && (this.activeCommand ? false : this.redoStack.length > 0)
  }

  /**
   * 设置锁定状态，锁定后撤销/重做将被屏蔽。
   *
   * @param val - 是否锁定
   */
  setLock(val: boolean) {
    this.isLocked = val
    this.notify()
  }

  /**
   * 开始一个交互式操作，命令会被视为活动命令。
   *
   * @param command - 正在进行中的命令
   */
  startCommand(command: Command) {
    this.activeCommand = command
    this.notify()
  }

  /** 结束并提交交互式操作 */
  commitCommand() {
    if (!this.activeCommand) {
      return
    }
    if (this.activeCommand.canCommit()) {
      this.addCommandAndExecute(this.activeCommand)
    }
    this.activeCommand = null
    this.notify()
  }

  /**
   * 执行并记录新操作。
   *
   * @param command - 要执行并入栈的命令
   */
  addCommandAndExecute(command: Command) {
    command.execute()
    this.addCommand(command)
    this.notify()
  }

  /**
   * 仅将命令入栈而不执行；同时清空重做栈。
   *
   * @param command - 要记录的命令
   */
  addCommand(command: Command) {
    this.undoStack.push(command)
    this.clearRedoStack() // 新操作会清空重做栈
    if (this.undoStack.length > this.maxLimit) {
      this.undoStack.shift()
    }
    this.notify()
  }

  /** 撤销一步：优先撤销活动命令的可逆步骤，否则弹出撤销栈。 */
  undo() {
    if (this.isLocked) return

    // 优先撤销正在进行中的命令
    if (this.activeCommand) {
      if (this.activeCommand.canUndoStep?.()) {
        this.activeCommand.undoStep?.()
        this.notify()
      }
      return
    }

    const command = this.undoStack.pop()
    if (command) {
      command.undo()
      this.redoStack.push(command)
      this.notify()
      this.undoRedoNotify()
    }
  }

  /** 重做一步：弹出重做栈并执行。活动命令存在时不允许重做。 */
  redo() {
    if (this.isLocked) return

    // 正在进行中的命令不能重做
    if (this.activeCommand) return

    const command = this.redoStack.pop()
    if (command) {
      command.redo()
      this.undoStack.push(command)
      this.notify()
      this.undoRedoNotify()
    }
  }

  /** 清空撤销与重做栈。 */
  clear() {
    this.undoStack = []
    this.redoStack = []
    this.notify()
  }

  private clearRedoStack() {
    this.redoStack = []
  }

  // 通知状态变化
  private notify() {
    this.emit('onStateChanged')
  }

  // 执行了撤销/重做操作后的通知（可用于视图刷新）
  private undoRedoNotify() {
    this.emit('undoRedo')
  }

  /** 主动触发一次状态变化通知，用于强制刷新 UI。 */
  refresh() {
    this.notify()
  }
}
